import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Typography,
  Card,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  Divider,
  LinearProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import ShareIcon from '@mui/icons-material/Share'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { toast } from 'react-toastify'
import MediaGeneratorService from '../../services/MediaGeneratorService'

const StyledPaper = styled(Paper)(() => ({
  padding: '24px',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  background: '#fff',
}))

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#f8f9fa',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
}))

const ResultCard = styled(Card)(() => ({
  height: 400,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  },
}))

const ActionButton = styled(IconButton)(() => ({
  backgroundColor: '#fff',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: '#f8f9fa',
  },
}))

const VideosContainer = () => {
  const [formData, setFormData] = useState({
    project: 'Mrx en el cielo',
    prompt: '',
    size: '9:16',
    reference: '',
    model: 'veo-2.0-generate-001'
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [wsConnection, setWsConnection] = useState(null)
  const [generatedVideos, setGeneratedVideos] = useState([])
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [tokens, setTokens] = useState({ used: 0, total: 0 })

  const fetchTokens = async () => {
    try {
      const response = await MediaGeneratorService.getTokens()
      setTokens(response)
    } catch (err) {
      console.error('Error fetching tokens:', err)
    }
  }

  // Fetch tokens when component mounts
  useEffect(() => {
    fetchTokens()
  }, [])

  // Cleanup WebSocket connection on component unmount
  useEffect(() => () => {
      if (wsConnection) {
        wsConnection.close()
      }
    }, [wsConnection])

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    })
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData({
          ...formData,
          reference: reader.result
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleWebSocketMessage = (message) => {
    try {
      const data = JSON.parse(message)
      console.log('Parsed WebSocket message:', data)
      
      if (data.status === 'running') {
        setProgress(data.data.progress)
        setProgressMessage(data.data.message)
      }
      
      if (data.status === 'done') {
        const mediaUrls = data.data?.media || []
        console.log('Setting videos:', mediaUrls)
        setGeneratedVideos(mediaUrls)
        setSuccess(true)
        setProgress(0)
        setProgressMessage('')
        
        // Actualizar tokens después de completar la generación
        fetchTokens()
        
        // Forzar el cierre del WebSocket
        wsConnection?.close()
        setWsConnection(null)
      }

      if (data.status === 'error') {
        console.error('WebSocket error:', data.data)
        toast.error(data.data || 'Error al procesar la generación del video', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        setError(data.data || 'Error al procesar la generación del video')
        setProgress(0)
        setProgressMessage('')
        
        // Cerrar el WebSocket en caso de error
        wsConnection?.close()
        setWsConnection(null)
      }
    } catch (err) {
      console.error('Error processing WebSocket message:', err)
      setError('Error al procesar la respuesta del servidor')
    }
  }

  const handleGenerate = async () => {
    try {
      setLoading(true)
      setError(null)
      setGeneratedVideos([]) // Reset videos on new generation
      setProgress(1) // Set initial progress to show the bar
      
      // Close previous WebSocket connection if exists
      if (wsConnection) {
        wsConnection.close()
        setWsConnection(null)
      }
      console.log('Form data:', formData)
      const response = await MediaGeneratorService.generateVideo(formData)
      console.log('Generated video response:', response)

      // Connect to WebSocket with the received job-id
      if (response.job_id) {
        const ws = MediaGeneratorService.connectToWebSocket(
          response.job_id,
          handleWebSocketMessage
        )
        setWsConnection(ws)
      }

      // Scroll to the progress bar
      const progressBar = document.querySelector('.progress-bar-container')
      if (progressBar) {
        progressBar.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (err) {
      setError(err.message || 'Error al generar el video')
      setSuccess(false)
      setProgress(0) // Reset progress on error
    } finally {
      setLoading(false)
    }
  }

  const handleCloseSnackbar = () => {
    setError(null)
    setSuccess(false)
  }

  return (
    <div className="progress-bar-container" 
    style={{
      marginTop: '-50px',
      transform: 'scale(0.9)'
    }}
    >
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {progress > 0 && (
        <Box 
          className="progress-bar-container"
          sx={{ 
            width: '100%', 
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            py: 2,
          }}
        >
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }}
          />
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 1, textAlign: 'center' }}
          >
            {progressMessage || 'Generando video...'}
          </Typography>
        </Box>
      )}

      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Proyecto</InputLabel>
          <Select
            value={formData.project}
            onChange={handleInputChange('project')}
            label="Proyecto"
            size="small"
          >
            <MenuItem value="Mrx en el cielo">Mrx en el cielo</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Tokens: {tokens.used}/{tokens.total}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <StyledPaper>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="600" sx={{ mb: 1 }}>
                Video Prompt
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Describe el video que deseas generar con el mayor detalle posible
              </Typography>
              <StyledTextField
                fullWidth
                multiline
                rows={4}
                placeholder="Un video vibrante de una Victory one recorriendo una calle soleada bordeada de palmeras..."
                value={formData.prompt}
                onChange={handleInputChange('prompt')}
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Modelo</InputLabel>
                  <Select
                    value={formData.model}
                    onChange={handleInputChange('model')}
                    label="Modelo"
                  >
                    <MenuItem value="veo-2.0-generate-001">Veo2 Experimental</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Formato</Typography>
              <RadioGroup
                row
                value={formData.size}
                onChange={handleInputChange('size')}
              >
                <FormControlLabel 
                  value="9:16" 
                  control={
                    <Radio 
                      sx={{
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  } 
                  label="Vertical (9:16)" 
                />
                <FormControlLabel 
                  value="16:9" 
                  control={
                    <Radio 
                      sx={{
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  } 
                  label="Horizontal (16:9)" 
                />
              </RadioGroup>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}
              >
                Imagen de referencia
              </Typography>
              <Box
                sx={{
                  border: '1px dashed rgba(0, 0, 0, 0.2)',
                  borderRadius: 1,
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#fafafa',
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                  },
                }}
                component="label"
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileUpload}
                />
                {formData.reference ? (
                  <Box 
                    component="img" 
                    src={formData.reference}
                    alt="Imagen cargada"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <>
                    <Box 
                      sx={{
                        width: '80px',
                        height: '80px',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      Imagen cargada
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Soporta: JPG, PNG (max. 5MB)
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGenerate}
                disabled={loading || progress > 0}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                {loading || progress > 0 ? <CircularProgress size={24} /> : 'Generar video'}
              </Button>
            </Box>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={{ mb: 2 }}>Resultados</Typography>
          <Grid container spacing={2}>
            {generatedVideos.length > 0 ? (
              generatedVideos.map((videoUrl) => (
                <Grid item xs={12} key={videoUrl}>
                  <ResultCard>
                    <Box sx={{ 
                      p: 2, 
                      flexGrow: 1, 
                      height: 'calc(400px - 56px)', // 56px es la altura de la barra de botones
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <video
                        controls
                        width="100%"
                        height="100%"
                        style={{ 
                          borderRadius: 8,
                          objectFit: 'contain',
                          maxHeight: '100%'
                        }}
                      >
                        <source src={videoUrl} type="video/mp4" />
                        <track kind="captions" label="Español" srcLang="es" default />
                        Tu navegador no soporta el elemento de video.
                      </video>
                    </Box>
                    <Divider />
                    <Box sx={{ 
                      p: 1.5, 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      gap: 1, 
                      bgcolor: '#f8f9fa',
                      height: '56px'
                    }}>
                      <ActionButton
                        size="small"
                        onClick={() => window.open(videoUrl, '_blank')}
                        title="Descargar"
                      >
                        <DownloadIcon fontSize="small" />
                      </ActionButton>
                      <ActionButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(videoUrl);
                          setSuccess(true);
                        }}
                        title="Copiar enlace"
                      >
                        <ShareIcon fontSize="small" />
                      </ActionButton>
                      <ActionButton
                        size="small"
                        onClick={() => window.location.reload()}
                        title="Generar nuevo"
                      >
                        <RefreshIcon fontSize="small" />
                      </ActionButton>
                    </Box>
                  </ResultCard>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <ResultCard>
                  <Box sx={{ p: 2, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      El video generado aparecerá aquí
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, bgcolor: '#f8f9fa' }}>
                    <ActionButton size="small">
                      <RefreshIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton size="small">
                      <DownloadIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton size="small">
                      <ShareIcon fontSize="small" />
                    </ActionButton>
                  </Box>
                </ResultCard>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success === true ? 'Enlace copiado al portapapeles' : 'Video generado exitosamente'}
        </Alert>
      </Snackbar>
    </Box>
    </div>
  )
}

export default VideosContainer 